import { useState } from "react";
import { useSession } from "@/frontend/auth-client";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/frontend/components/ui/avatar";
import { Separator } from "@/frontend/components/ui/separator";
import {
  Crown,
  Loader2,
  Mail,
  Trash2
} from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from "@/frontend/components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/frontend/components/ui/select";
import { Field, FieldLabel, FieldGroup, FieldDescription } from "@/frontend/components/ui/field";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/frontend/components/ui/tooltip";
import { cn } from "@/frontend/lib/utils";
import { getInitials } from "@/frontend/utils/shared";
import { getRoleIcon, getRoleLabel, canModifyMember, assignableRoles } from "@/frontend/utils/auth";
import {
  useActiveMemberQuery,
  useMembersQuery,
  useInvitationsQuery,
} from "@/frontend/queries/dashboard/settings";
import {
  useCreateInvitationMutation,
  useUpdateMemberRoleMutation,
  useRemoveMemberMutation as useRemoveOrgMemberMutation,
  useDeleteInvitationMutation,
} from "@/frontend/mutations/dashboard/settings";

export function MembersSection() {
  const { data: session } = useSession();
  const isPlatformAdmin = session?.user?.role === "admin";
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [showRemoveDialog, setShowRemoveDialog] = useState<string | null>(null);
  const [showDeleteInviteDialog, setShowDeleteInviteDialog] = useState<string | null>(null);
  const [showRoleDialog, setShowRoleDialog] = useState<{ id: string; currentRole: string } | null>(null);
  const [newRole, setNewRole] = useState("");

  const activeMemberQuery = useActiveMemberQuery(!!session);
  const membersQuery = useMembersQuery();

  const memberRole = activeMemberQuery.data?.role ?? "";
  const myRoles = memberRole.split(",").map((r) => r.trim()).filter(Boolean);
  const canInvite = isPlatformAdmin || myRoles.includes("admin") || myRoles.includes("owner");
  const canManageMembers = isPlatformAdmin || myRoles.includes("admin") || myRoles.includes("owner");

  const invitationsQuery = useInvitationsQuery(canInvite);

  const inviteMutation = useCreateInvitationMutation(() => {
    setShowInviteDialog(false);
    setInviteEmail("");
    setInviteRole("member");
  });

  const updateRoleMutation = useUpdateMemberRoleMutation(() => setShowRoleDialog(null));
  const removeMemberMutation = useRemoveOrgMemberMutation(() => setShowRemoveDialog(null));
  const deleteInvitationMutation = useDeleteInvitationMutation(() => setShowDeleteInviteDialog(null));

  const members = membersQuery.data?.members ?? [];
  const invitations = invitationsQuery.data ?? [];

  /** Group members: admins (includes owners) first, then members. */
  const primaryRole = (r: string) =>
    (r.split(",").map((s) => s.trim()).filter(Boolean)[0] ?? r).toLowerCase();
  const roleOrder = ["admin", "member"] as const;
  const groupedMembers = roleOrder.map((role) => ({
    role,
    members: members.filter((m) => {
      const pr = primaryRole(m.role);
      return role === "admin" ? (pr === "admin" || pr === "owner") : pr === role;
    }),
  })).filter((g) => g.members.length > 0);

  return (
    <>
      <div className="flex flex-col gap-8">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold">Members</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manage who has access to your workspace.
            </p>
          </div>
          {canInvite && (
            <Button onClick={() => setShowInviteDialog(true)}>
              <Mail className="size-4 mr-2" />
              Invite member
            </Button>
          )}
        </div>

        <Separator />

        {/* Members table grouped by role */}
        {membersQuery.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {groupedMembers.map(({ role, members: groupMembers }) => (
              <div key={role} className="flex flex-col">
                <div className="rounded-lg border divide-y">
                  <div className="px-4 py-2.5 bg-muted/40 border-b">
                    <p className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      {getRoleIcon(role)}
                      {getRoleLabel(role)} ({groupMembers.length})
                    </p>
                  </div>
                  {groupMembers.map((member) => (
                    <div key={member.id} className="group flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
                      <Avatar className="size-8 shrink-0">
                        <AvatarImage src={member.user.image || undefined} />
                        <AvatarFallback className="text-xs" data-ph-mask>
                          {getInitials(member.user.name, member.user.email)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate leading-tight flex items-center gap-1">
                          <span data-ph-mask>{member.user.name || "No name"}</span>
                          {member.userId === session?.user?.id && (
                            <span className="text-xs text-muted-foreground font-normal">(you)</span>
                          )}
                          {primaryRole(member.role) === "owner" && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Crown className="size-3.5 text-amber-500 shrink-0" />
                                </TooltipTrigger>
                                <TooltipContent>Owner</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground truncate" data-ph-mask>{member.user.email}</p>
                      </div>
                      {canManageMembers &&
                        member.userId !== session?.user?.id &&
                        canModifyMember(myRoles, member.role, isPlatformAdmin) && (
                          <div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                const options = assignableRoles(myRoles, isPlatformAdmin);
                                setNewRole(
                                  options.includes(member.role as "member" | "admin" | "owner")
                                    ? member.role
                                    : options[0] ?? "member"
                                );
                                setShowRoleDialog({ id: member.id, currentRole: member.role });
                              }}
                            >
                              Change role
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => setShowRemoveDialog(member.id)}
                            >
                              Remove
                            </Button>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Invitations table — pending and accepted, visible to admins/owners */}
        {canInvite && invitations.length > 0 && (
          <>
            <Separator />
            <div className="flex flex-col gap-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Invitations
              </p>
              <div className="rounded-lg border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/40">
                      <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Email</th>
                      <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Role</th>
                      <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">Status</th>
                      {canManageMembers && <th className="px-4 py-2.5 w-16" />}
                    </tr>
                  </thead>
                  <tbody>
                    {(invitations as { id: string; email: string; role: string; status: string }[]).map((invitation) => (
                      <tr key={invitation.id} className="group border-b last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3 font-medium" data-ph-mask>{invitation.email}</td>
                        <td className="px-4 py-3 text-muted-foreground">{getRoleLabel(invitation.role)}</td>
                        <td className="px-4 py-3">
                          <span
                            className={cn(
                              "inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium capitalize",
                              invitation.status === "accepted"
                                ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
                                : invitation.status === "expired"
                                  ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                                  : invitation.status === "revoked"
                                    ? "bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                                    : "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
                            )}
                          >
                            {invitation.status}
                          </span>
                        </td>
                        {canManageMembers && (
                          <td className="px-4 py-3">
                            {invitation.status === "pending" ? (
                              <div className="flex justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => setShowDeleteInviteDialog(invitation.id)}
                                >
                                  <Trash2 className="size-3.5" />
                                </Button>
                              </div>
                            ) : null}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite member</DialogTitle>
            <DialogDescription>
              Send an invitation email to add a new member to your workspace.
            </DialogDescription>
          </DialogHeader>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="invite-email">Email address</FieldLabel>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@example.com"
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="invite-role">Role</FieldLabel>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <FieldDescription>
                Admins can manage members and workspace settings.
              </FieldDescription>
            </Field>
          </FieldGroup>
          {inviteMutation.isError && (
            <p className="text-sm text-destructive">{inviteMutation.error.message}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => inviteMutation.mutate({ email: inviteEmail, role: inviteRole as "member" | "admin" })}
              disabled={inviteMutation.isPending || !inviteEmail}
            >
              {inviteMutation.isPending ? (
                <><Loader2 className="animate-spin" data-icon="inline-start" />Sending…</>
              ) : "Send invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={!!showRoleDialog} onOpenChange={() => setShowRoleDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change member role</DialogTitle>
            <DialogDescription>Select a new role for this member.</DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel>New role</FieldLabel>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {assignableRoles(myRoles, isPlatformAdmin).map((r) => (
                  <SelectItem key={r} value={r}>
                    {getRoleLabel(r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleDialog(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (showRoleDialog) updateRoleMutation.mutate({ memberId: showRoleDialog.id, role: newRole as "member" | "admin" | "owner" });
              }}
              disabled={updateRoleMutation.isPending}
            >
              {updateRoleMutation.isPending ? (
                <><Loader2 className="animate-spin" data-icon="inline-start" />Updating…</>
              ) : "Update role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Member Dialog */}
      <Dialog
        open={!!showRemoveDialog}
        onOpenChange={(open) => { if (!open) { setShowRemoveDialog(null); removeMemberMutation.reset(); } }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this member from the workspace? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {removeMemberMutation.isError && (
            <p className="text-sm text-destructive">
              {removeMemberMutation.error instanceof Error
                ? removeMemberMutation.error.message
                : "Failed to remove member"}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemoveDialog(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => { if (showRemoveDialog) removeMemberMutation.mutate({ memberIdOrEmail: showRemoveDialog }); }}
              disabled={removeMemberMutation.isPending}
            >
              {removeMemberMutation.isPending ? (
                <><Loader2 className="animate-spin" data-icon="inline-start" />Removing…</>
              ) : "Remove member"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Invitation Dialog */}
      <Dialog
        open={!!showDeleteInviteDialog}
        onOpenChange={(open) => { if (!open) { setShowDeleteInviteDialog(null); deleteInvitationMutation.reset(); } }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete invitation</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this invitation? The recipient will no longer be able to join the workspace with this invite.
            </DialogDescription>
          </DialogHeader>
          {deleteInvitationMutation.isError && (
            <p className="text-sm text-destructive">
              {deleteInvitationMutation.error instanceof Error
                ? deleteInvitationMutation.error.message
                : "Failed to delete invitation"}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeleteInviteDialog(null); deleteInvitationMutation.reset(); }}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => { if (showDeleteInviteDialog) deleteInvitationMutation.mutate({ invitationId: showDeleteInviteDialog }); }}
              disabled={deleteInvitationMutation.isPending}
            >
              {deleteInvitationMutation.isPending ? (
                <><Loader2 className="animate-spin" data-icon="inline-start" />Deleting…</>
              ) : "Delete invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
